import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { MatrixError, Room } from '$types/matrix-sdk';
import { JoinRule, EventType, RoomType } from '$types/matrix-sdk';
import { Avatar, Badge, Box, Button, Dialog, Spinner, Text, as, color, config, toRem } from 'folds';
import classNames from 'classnames';
import { userFallbackIcon } from '$components/icons/phosphor';
import { getMxIdLocalPart, mxcUrlToHttp } from '$utils/matrix';
import { nameInitials } from '$utils/common';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { AsyncStatus, useAsyncCallback } from '$hooks/useAsyncCallback';
import { onEnterOrSpace } from '$utils/keyboard';

import { useJoinedRoomId } from '$hooks/useJoinedRoomId';
import { useElementSizeObserver } from '$hooks/useElementSizeObserver';
import { getRoomAvatarUrl } from '$utils/room/display';
import { getStateEvent } from '$utils/room/hierarchy';
import { useStateEventCallback } from '$hooks/useStateEventCallback';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { KnockRoomPrompt } from '$components/knock-room-prompt';
import { RoomAvatar } from '$components/room-avatar';
import { formatCompactNumber } from '$utils/formatCompactNumber';
import * as css from './style.css';
import type { RoomBannerContent } from '$types/matrix-sdk-events';
import { CustomStateEvent } from '$types/matrix/room';
import colorMXID from '$utils/colorMXID';
import { reportMediaLoadFailure } from '$utils/mediaLoadDiagnostics';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';
import { Image as MediaImage } from '$components/media';

type GridColumnCount = '1' | '2' | '3';
const getGridColumnCount = (gridWidth: number): GridColumnCount => {
  if (gridWidth <= 498) return '1';
  if (gridWidth <= 748) return '2';
  return '3';
};

const setGridColumnCount = (grid: HTMLElement, count: GridColumnCount): void => {
  grid.style.setProperty('grid-template-columns', `repeat(${count}, 1fr)`);
};

export function RoomCardGrid({ children }: { children: ReactNode }) {
  const gridRef = useRef<HTMLDivElement>(null);

  useElementSizeObserver(
    useCallback(() => gridRef.current, []),
    useCallback((width, _, target) => setGridColumnCount(target, getGridColumnCount(width)), [])
  );

  return (
    <Box className={css.CardGrid} direction="Row" gap="400" wrap="Wrap" ref={gridRef}>
      {children}
    </Box>
  );
}

export const RoomCardBase = as<'div'>(({ className, ...props }, ref) => (
  <Box
    direction="Column"
    className={classNames(css.RoomCardBase, className)}
    {...props}
    ref={ref}
  />
));

const RoomCardName = as<'h6'>(({ children, ...props }, ref) => (
  <Text as="h6" size="H6" truncate {...props} ref={ref}>
    {children}
  </Text>
));

const RoomCardTopic = as<'p'>(({ children, className, ...props }, ref) => (
  <Text
    as="p"
    size="T200"
    className={classNames(css.RoomCardTopic, className)}
    {...props}
    priority="400"
    ref={ref}
  >
    {children}
  </Text>
));

function ErrorDialog({
  title,
  message,
  children,
}: {
  title: string;
  message: string;
  children: (openError: () => void) => ReactNode;
}) {
  const [viewError, setViewError] = useState(false);
  const closeError = () => setViewError(false);
  const openError = () => setViewError(true);

  return (
    <>
      {children(openError)}
      <ModalOverlay open={viewError} requestClose={closeError}>
        <Dialog variant="Surface">
          <Box style={{ padding: config.space.S400 }} direction="Column" gap="400">
            <Box direction="Column" gap="100">
              <Text>{title}</Text>
              <Text style={{ color: color.Critical.Main }} size="T300" priority="400">
                {message}
              </Text>
            </Box>
            <Button size="400" variant="Secondary" fill="Soft" onClick={closeError}>
              <Text size="B400">Cancel</Text>
            </Button>
          </Box>
        </Dialog>
      </ModalOverlay>
    </>
  );
}

type RoomCardProps = {
  roomIdOrAlias: string;
  allRooms: string[];
  avatarUrl?: string;
  name?: string;
  topic?: string;
  memberCount?: number;
  roomType?: string;
  joinRule?: JoinRule;
  viaServers?: string[];
  onView?: (roomId: string) => void;
  renderTopicViewer: (name: string, topic: string, requestClose: () => void) => ReactNode;
};

export const RoomCard = as<'div', RoomCardProps>(
  (
    {
      roomIdOrAlias,
      allRooms,
      avatarUrl,
      name,
      topic,
      memberCount,
      roomType,
      joinRule,
      viaServers,
      onView,
      renderTopicViewer,
      ...props
    },
    ref
  ) => {
    const mx = useMatrixClient();
    const useAuthentication = useMediaAuthentication();
    const joinedRoomId = useJoinedRoomId(allRooms, roomIdOrAlias);
    const joinedRoom = mx.getRoom(joinedRoomId);
    const [topicEvent, setTopicEvent] = useState(() =>
      joinedRoom ? getStateEvent(joinedRoom, EventType.RoomTopic) : undefined
    );
    const [knocking, setKnocking] = useState(false);
    const fallbackName = getMxIdLocalPart(roomIdOrAlias) ?? roomIdOrAlias;
    const fallbackTopic = roomIdOrAlias;

    const avatar = joinedRoom
      ? getRoomAvatarUrl(mx, joinedRoom, 96, useAuthentication)
      : avatarUrl && mxcUrlToHttp(mx, avatarUrl, useAuthentication, 96, 96, 'crop');

    const bannerState = joinedRoom
      ? getStateEvent(joinedRoom, CustomStateEvent.RoomBanner)
      : undefined;
    const bannerMXC = bannerState?.getContent<RoomBannerContent>()?.url;
    const bannerURI = mxcUrlToHttp(mx, bannerMXC ?? '', useAuthentication);
    const roomName = joinedRoom?.name || name || fallbackName;
    const roomTopic =
      (topicEvent?.getContent().topic as string) || undefined || topic || fallbackTopic;
    const joinedMemberCount = joinedRoom?.getJoinedMemberCount() ?? memberCount;

    useStateEventCallback(
      mx,
      useCallback(
        (event) => {
          if (
            joinedRoom &&
            event.getRoomId() === joinedRoom.roomId &&
            event.getType() === (EventType.RoomTopic as string)
          ) {
            setTopicEvent(getStateEvent(joinedRoom, EventType.RoomTopic));
          }
        },
        [joinedRoom]
      )
    );

    const [joinState, join] = useAsyncCallback<Room, MatrixError, []>(
      useCallback(() => mx.joinRoom(roomIdOrAlias, { viaServers }), [mx, roomIdOrAlias, viaServers])
    );
    const joining =
      joinState.status === AsyncStatus.Loading || joinState.status === AsyncStatus.Success;

    const [viewTopic, setViewTopic] = useState(false);
    const closeTopic = () => setViewTopic(false);
    const openTopic = () => setViewTopic(true);
    return (
      <RoomCardBase {...props} ref={ref}>
        <Box style={{ height: toRem(120) }} direction="Column">
          {!bannerURI && !avatar ? (
            <span
              className={css.RoomCardBanner({ trueBanner: false })}
              style={{
                backgroundColor: colorMXID(roomIdOrAlias),
              }}
            />
          ) : (
            <MediaImage
              className={css.RoomCardBanner({ trueBanner: !!bannerURI })}
              src={bannerURI || avatar || undefined}
              alt={`${name} cover`}
              draggable="false"
              onError={() => reportMediaLoadFailure('room_card_banner')}
            />
          )}
          <Avatar className={css.RoomCardAvatar} size="500">
            <RoomAvatar
              roomId={roomIdOrAlias}
              src={avatar ?? undefined}
              alt={roomIdOrAlias}
              renderFallback={() => (
                <Text as="span" size="H3">
                  {nameInitials(roomName)}
                </Text>
              )}
            />
          </Avatar>
        </Box>
        <Box className={css.RoomCardItems} direction="Column" gap="300">
          <Box gap="200" justifyContent="SpaceBetween">
            <Box grow="Yes" direction="Column" gap="100">
              <RoomCardName>{roomName}</RoomCardName>
              <RoomCardTopic onClick={openTopic} onKeyDown={onEnterOrSpace(openTopic)} tabIndex={0}>
                {roomTopic}
              </RoomCardTopic>
            </Box>
            <ModalOverlay open={viewTopic} requestClose={closeTopic}>
              {renderTopicViewer(roomName, roomTopic, closeTopic)}
            </ModalOverlay>
            {(roomType === RoomType.Space || joinedRoom?.isSpaceRoom()) && (
              <Badge variant="Secondary" fill="Soft" outlined>
                <Text size="L400">Space</Text>
              </Badge>
            )}
          </Box>
          {typeof joinedMemberCount === 'number' && (
            <Box gap="100">
              {userFallbackIcon('sm')}
              <Text size="T200">{`${formatCompactNumber(joinedMemberCount)} Members`}</Text>
            </Box>
          )}
          {typeof joinedRoomId === 'string' && (
            <Button
              onClick={onView ? () => onView(joinedRoomId) : undefined}
              variant="Secondary"
              fill="Soft"
              size="300"
            >
              <Text size="B300" truncate>
                View
              </Text>
            </Button>
          )}
          {typeof joinedRoomId !== 'string' &&
            joinState.status !== AsyncStatus.Error &&
            (joinRule === JoinRule.Knock ? (
              <>
                <Button onClick={() => setKnocking(true)} variant="Secondary" size="300">
                  <Text size="B300" truncate>
                    Knock
                  </Text>
                </Button>

                {knocking && (
                  <KnockRoomPrompt
                    roomId={roomIdOrAlias}
                    via={viaServers}
                    onDone={() => setKnocking(false)}
                    onCancel={() => setKnocking(false)}
                  />
                )}
              </>
            ) : (
              <Button
                onClick={join}
                variant="Secondary"
                size="300"
                disabled={joining}
                before={
                  joining && (
                    <Spinner
                      size="50"
                      variant="Secondary"
                      fill="Soft"
                      style={{ background: 'transparent' }}
                    />
                  )
                }
              >
                <Text size="B300" truncate>
                  {joining ? 'Joining' : 'Join'}
                </Text>
              </Button>
            ))}
          {typeof joinedRoomId !== 'string' && joinState.status === AsyncStatus.Error && (
            <Box gap="200">
              <Button
                onClick={join}
                className={css.ActionButton}
                variant="Critical"
                fill="Solid"
                size="300"
              >
                <Text size="B300" truncate>
                  Retry
                </Text>
              </Button>
              <ErrorDialog
                title="Join Error"
                message={joinState.error.message || 'Failed to join. Unknown Error.'}
              >
                {(openError) => (
                  <Button
                    onClick={openError}
                    className={css.ActionButton}
                    variant="Critical"
                    fill="Soft"
                    outlined
                    size="300"
                  >
                    <Text size="B300" truncate>
                      View Error
                    </Text>
                  </Button>
                )}
              </ErrorDialog>
            </Box>
          )}
        </Box>
      </RoomCardBase>
    );
  }
);
